import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/ui/password-input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const ROLE_LABEL = {
  diretor: "Diretor",
  gestor: "Gestor de reservas",
  financeiro: "Financeiro",
  operacional: "Operacional",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", role: "operacional", password: "" });

  async function load() {
    const r = await api.get("/admin/users");
    setUsers(r.data);
  }
  useEffect(() => { load(); }, []);

  async function create() {
    try {
      await api.post("/admin/users", newUser);
      toast.success("Usuário criado");
      setCreating(false);
      setNewUser({ email: "", name: "", role: "operacional", password: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  }

  async function saveEdit() {
    const { id, ...body } = editing;
    if (!body.password) delete body.password;
    await api.patch(`/admin/users/${id}`, body);
    toast.success("Usuário atualizado");
    setEditing(null);
    load();
  }

  return (
    <div data-testid="admin-users-page">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Equipe</span>
          <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Usuários</h1>
        </div>
        <Button className="bg-[#0AA89C] hover:bg-[#066B63]" onClick={() => setCreating(true)} data-testid="new-user-btn">
          <Plus className="w-4 h-4 mr-1" /> Novo usuário
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">Nome</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">E-mail</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">Perfil</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">MFA</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">Ativo</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">{ROLE_LABEL[u.role] || u.role}</td>
                <td className="px-4 py-3">{u.mfa_enabled ? "Ativo" : "—"}</td>
                <td className="px-4 py-3">{u.active ? "Sim" : "Não"}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...u, password: "" })} data-testid={`edit-user-${u.email}`}>
                    Editar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Novo usuário</DialogTitle><DialogDescription>Cadastre um novo integrante da equipe com perfil e senha inicial.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nome</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" /></div>
            <div><Label>E-mail</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" /></div>
            <div>
              <Label>Perfil</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label htmlFor="new-user-password">Senha inicial (mín. 8 caracteres)</Label><PasswordInput id="new-user-password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="new-user-password" autoComplete="new-password" /></div>
            <Button className="w-full bg-[#0AA89C] hover:bg-[#066B63]" onClick={create} data-testid="new-user-submit">Criar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Editar usuário</DialogTitle><DialogDescription>Atualize nome, perfil, senha ou status de ativação do usuário.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Nome</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div>
                <Label>Perfil</Label>
                <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Nova senha (deixe em branco para manter)</Label><PasswordInput value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} autoComplete="new-password" /></div>
              <div className="flex items-center gap-3">
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Ativo</Label>
              </div>
              <Button className="w-full bg-[#0AA89C] hover:bg-[#066B63]" onClick={saveEdit}>Salvar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
